import { observer } from 'mobx-react-lite';
import { Loader2, Tag, AlertCircle, TriangleAlert, ChevronDown } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { TariffModal } from '@/controllers/clients/clientCard/useTariffModal';

type Props = { ctrl: TariffModal };

export const TariffChangeModal = observer(function TariffChangeModal({ ctrl }: Props) {
  return (
    <Modal
      isOpen={ctrl.isOpen}
      onClose={ctrl.close}
      title='Сменить тариф'
      subtitle={ctrl.cameraName}
      size='md'
      footer={
        <>
          <Button variant='outline' onClick={ctrl.close} className='flex-1'>
            Отмена
          </Button>
          <Button
            onClick={ctrl.submit}
            disabled={!ctrl.selectedTariffId || ctrl.isLoading}
            className='flex-1 gap-2'
          >
            {ctrl.isLoading ? (
              <Loader2 className='w-4 h-4 animate-spin' />
            ) : (
              <Tag className='w-4 h-4' />
            )}
            Применить
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div>
          <Label className='text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5'>
            Тариф
          </Label>
          <div className='relative'>
            <select
              value={ctrl.selectedTariffId}
              onChange={(e) => ctrl.setSelectedTariffId(e.target.value)}
              className='w-full px-3.5 py-2.5 pr-9 rounded-xl border border-border/50 bg-card text-sm text-foreground outline-none focus:border-primary/50 transition-colors appearance-none cursor-pointer'
            >
              <option value='' disabled>
                {ctrl.currentTariffName
                  ? `${ctrl.currentTariffName} (текущий)`
                  : '— выберите тариф —'}
              </option>
              {ctrl.filteredTariffs.map((t) => (
                <option key={t.id_tariff} value={String(t.id_tariff)}>
                  {t.tariff_name ?? '—'}
                  {t.full_ab_plata != null
                    ? ` — ${t.full_ab_plata.toLocaleString('ru-RU')} ${ctrl.countryCurrencyMap.get(t.country) ?? ctrl.currencyLabel}`
                    : ''}
                </option>
              ))}
            </select>
            <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
          </div>
        </div>
        {ctrl.isArchiveDowngrade && (
          <div className='flex items-start gap-2.5 p-3.5 bg-yellow-500/10 border border-yellow-500/40 rounded-xl text-yellow-500 text-sm'>
            <TriangleAlert className='w-4 h-4 shrink-0 mt-0.5' />
            <span>
              При понижении дней хранения тарифа уже записанный архив будет удалён. Смена тарифа
              рекомендуется только со следующего расчётного периода.
            </span>
          </div>
        )}
        <div>
          <Label className='text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5'>
            Дата
          </Label>
          <Input
            type='date'
            value={ctrl.date}
            min={ctrl.minDate}
            onChange={(e) => ctrl.setDate(e.target.value)}
            className='bg-muted/30 border-border/50 focus-visible:border-primary/50 focus-visible:ring-0'
          />
        </div>
        <div>
          <Label className='text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5'>
            Комментарий
          </Label>
          <Input
            type='text'
            placeholder='Необязательно'
            value={ctrl.description}
            onChange={(e) => ctrl.setDescription(e.target.value)}
            className='bg-muted/30 border-border/50 focus-visible:border-primary/50 focus-visible:ring-0'
          />
        </div>
        {ctrl.error && (
          <div className='flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-xl text-destructive text-sm'>
            <AlertCircle className='w-4 h-4 shrink-0' />
            {ctrl.error}
          </div>
        )}
      </div>
    </Modal>
  );
});
